// Mercer University: public college page (#2739), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Mercer's Common Data Set 2024-2025 (Office of Institutional
//    Effectiveness), the newest edition Mercer has posted: C1 applicants,
//    admits and enrollees for Fall 2024 with the residency table, C8 test
//    policy (written for Fall 2026 applicants; the PDF's form fields read
//    SAT or ACT "required" and a July 1 score date), C21/C22 plans (no early
//    decision; early action, which the CDS gives no counts for). Departure:
//    no 2025-2026 CDS is posted yet, so the counts are a year older than
//    most pages here, and labelled Fall 2024.
//  - undergrad.mercer.edu: First-Year Admissions (deadlines, decision
//    release, deposit date, the application checklist with test scores) and
//    the admissions office's post "Test Required and What It Means For You"
//    (the return to required scores). The deadlines carry no year label;
//    they are the page as posted for the application open now (Fall 2027
//    entry). Its FAFSA line still names the 2026-2027 FAFSA, so the page
//    states no FAFSA date. The page's test-optional checklist is commented
//    out of the HTML; the visible checklist asks for SAT or ACT scores.
//  - Mercer Student Financial Planning, Residential Undergraduate 2026-2027
//    Estimated Cost of Attendance. (The CDS G1 gives 2025-2026 charges.)
//  - College Scorecard API, school id 140447 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Mercer Common Data Set 2024-2025',
        url: 'https://oie.mercer.edu/www/mu-oie/research/upload/CDS-2024-2025_complete-2.pdf',
        read: '2026-10-01',
    },
    firstyear: {
        label: 'Mercer Admissions, First-Year Admissions',
        url: 'https://undergrad.mercer.edu/first-year-admissions/',
        read: '2026-10-01',
    },
    testreq: {
        label: 'Mercer Admissions, Test Required and What It Means For You',
        url: 'https://undergrad.mercer.edu/blog/test-required/',
        read: '2026-10-01',
    },
    coa: {
        label: 'Mercer Student Financial Planning, Residential Undergraduate Cost of Attendance',
        url: 'https://financialaid.mercer.edu/cost-of-attendance/residential-undergraduate/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?140447-Mercer-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'mercer-university',
    kbId: 'mercer_university',
    name: 'Mercer University',
    shortName: 'Mercer',
    location: 'Macon, GA',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Mercer University branding, typography and colors); decoration only.
    branding: {
        primary: '#F76800',
        secondary: '#222222',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/mercer_university_logo.png',
    },

    admission: {
        entering: 'Fall 2024',
        cdsCycle: '2024-2025',
        applied: 9813,
        admitted: 6760,
        enrolled: 859,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 7325, admitted: 5229 },
            { group: 'Out-of-state', applied: 1705, admitted: 1170 },
            { group: 'International', applied: 759, admitted: 349 },
            { group: 'Unknown', applied: 24, admitted: 12 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Mercer\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'Mercer has no early decision plan. Its early action rounds are not binding, and the Common Data Set gives no early action counts.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'February 1' },
        rows: [
            { plan: 'Early Action I', deadline: 'November 1', decision: 'Mid-November', binding: false },
            { plan: 'Early Action II', deadline: 'December 1', decision: 'Mid-December', binding: false },
            { plan: 'Regular Decision', deadline: 'February 1', decision: 'Within February', binding: false },
        ],
        note: 'The application opens August 1, and Mercer takes the Common Application or its own application with no preference between them. Applying by Early Action gives priority consideration for scholarships. Enrollment deposits are due May 1.',
        source: 'firstyear',
        binding: 'Early Action is not binding: you have until May 1 to decide.',
        bindingSource: 'firstyear',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-required',
        blind: false,
        line: 'Mercer requires the SAT or ACT. It returned to required scores with the fall 2026 application cycle, after five years test-optional. You may type your scores into the application and send official scores before you enroll. Mercer superscores, using your highest scores across every test you send.',
        source: 'testreq',
        cdsLine: 'The 2024-2025 Common Data Set, written for fall 2026 applicants, also marks the SAT or ACT as required to be considered for admission, with scores due by July 1.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstyear',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 44784 },
            { item: 'Facility and technology fee', amount: 500 },
            { item: 'Housing', amount: 8046 },
            { item: 'Food', amount: 7866 },
            { item: 'Books, course materials, supplies and equipment', amount: 1200 },
            { item: 'Transportation and travel', amount: 1206 },
            { item: 'Personal expenses', amount: 1710 },
            { item: 'Federal loan fees (average)', amount: 68 },
        ],
        total: 65380,
        note: 'For a full-time residential undergraduate, fall and spring. Students without their own health insurance are billed for Mercer\'s plan, $1,402 a semester, unless they waive it; lab, internship and private music lesson fees are extra when they apply.',
        source: 'coa',
        netPrice: {
            average: 24778,
            byIncome: [
                { income: '$0 to $30,000', amount: 18845 },
                { income: '$30,001 to $48,000', amount: 21273 },
                { income: '$48,001 to $75,000', amount: 22137 },
                { income: '$75,001 to $110,000', amount: 24117 },
                { income: 'Over $110,000', amount: 27667 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Mercer\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
